import { CHECKLIST_ITEM_TYPES, MAX_PHOTO_ANSWERS } from '../../../common/utils/checklist/checklistItems';

export const MAX_PHOTOS_PER_ITEM = MAX_PHOTO_ANSWERS;

// Dropdowns with a few very short options ("1".."5") show as a row of pills.
const MAX_PILL_OPTIONS = 5;
const MAX_PILL_OPTION_LENGTH = 3;
// Two-option answers use the Yes/No control only while each label fits it
// (beside the question when both are short); longer labels become full-width choices.
const MAX_YES_NO_LABEL_LENGTH = 12;
const MAX_INLINE_YES_NO_LENGTH = 14;

export const ITEM_CONTROL = {
	TOGGLE: 'toggle',
	YES_NO: 'yesNo',
	NUMBER: 'number',
	PILLS: 'pills',
	CHOICES: 'choices',
	SELECT: 'select',
	CHECKBOX_GROUP: 'checkboxGroup',
	TEXT: 'text',
	PHOTO: 'photo',
};

// row: question left, control right; stacked: control under the question (same card); block: label, then a wide control.
export const ITEM_LAYOUT = {
	ROW: 'row',
	STACKED: 'stacked',
	BLOCK: 'block',
};

export const isAnswerProvided = (item, value) => {
	switch (item.type) {
		case CHECKLIST_ITEM_TYPES.CHECKBOX:
			return value === true;
		case CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP:
			return Array.isArray(value) && value.length > 0;
		case CHECKLIST_ITEM_TYPES.NUMBER:
			return value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value));
		case CHECKLIST_ITEM_TYPES.PHOTO:
			return Array.isArray(value) && value.length > 0;
		default:
			return value !== undefined && value !== null && String(value).trim() !== '';
	}
};

export const getNumberValidationError = (item, value) => {
	if (value === undefined || value === null || value === '') {
		return null;
	}

	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return 'Enter a valid number';
	}

	if (Number.isFinite(Number(item.min)) && numericValue < Number(item.min)) {
		return `Must be at least ${item.min}`;
	}

	if (Number.isFinite(Number(item.max)) && numericValue > Number(item.max)) {
		return `Must be at most ${item.max}`;
	}

	return null;
};

/**
 * How an item is answered. Templates have no Yes/No type: a two-option
 * dropdown is a Yes/No with the template's own labels ("Yes / Not needed"),
 * and an optional checkbox phrased as a question ("Is the vehicle due for
 * service?") is a plain Yes/No. Required checkboxes stay toggles because they
 * must be switched on to submit.
 */
export const getItemControl = (item) => {
	const options = item.options || [];
	switch (item.type) {
		case CHECKLIST_ITEM_TYPES.CHECKBOX:
			return !item.required && /\?\s*$/.test(item.label || '') ? ITEM_CONTROL.YES_NO : ITEM_CONTROL.TOGGLE;
		case CHECKLIST_ITEM_TYPES.DROPDOWN:
			if (options.length === 2) {
				return options.every(option => option.length <= MAX_YES_NO_LABEL_LENGTH)
					? ITEM_CONTROL.YES_NO
					: ITEM_CONTROL.CHOICES;
			}
			return options.length > 2 &&
				options.length <= MAX_PILL_OPTIONS &&
				options.every(option => option.length <= MAX_PILL_OPTION_LENGTH)
				? ITEM_CONTROL.PILLS
				: ITEM_CONTROL.SELECT;
		case CHECKLIST_ITEM_TYPES.CHECKBOX_GROUP:
			return ITEM_CONTROL.CHECKBOX_GROUP;
		case CHECKLIST_ITEM_TYPES.NUMBER:
			return ITEM_CONTROL.NUMBER;
		case CHECKLIST_ITEM_TYPES.TEXT:
			return ITEM_CONTROL.TEXT;
		case CHECKLIST_ITEM_TYPES.PHOTO:
			return ITEM_CONTROL.PHOTO;
		default:
			return null;
	}
};

export const getItemLayout = (item) => {
	const control = getItemControl(item);
	if (control === ITEM_CONTROL.YES_NO) {
		const labelLength = item.type === CHECKLIST_ITEM_TYPES.DROPDOWN
			? item.options[0].length + item.options[1].length
			: 0;
		return labelLength > MAX_INLINE_YES_NO_LENGTH ? ITEM_LAYOUT.STACKED : ITEM_LAYOUT.ROW;
	}
	if (control === ITEM_CONTROL.CHOICES) {
		return ITEM_LAYOUT.STACKED;
	}

	return [ITEM_CONTROL.TOGGLE, ITEM_CONTROL.NUMBER].includes(control) ? ITEM_LAYOUT.ROW : ITEM_LAYOUT.BLOCK;
};

// Section counts treat an explicit "No" on a Yes/No checkbox as answered.
export const isItemAnswered = (item, value) => (
	item.type === CHECKLIST_ITEM_TYPES.CHECKBOX && getItemControl(item) === ITEM_CONTROL.YES_NO
		? typeof value === 'boolean'
		: isAnswerProvided(item, value)
);

// Headings split the visible items into sections; items before the first heading form an untitled one.
export const groupIntoSections = items => items.reduce((sections, item) => {
	if (item.type === CHECKLIST_ITEM_TYPES.HEADING) {
		sections.push({ key: item.id, title: item.label, items: [] });
	}
	else if (!sections.length) {
		sections.push({ key: 'intro', title: null, items: [item] });
	}
	else {
		sections[sections.length - 1].items.push(item);
	}
	return sections;
}, []);
